import { Link, useNavigate } from 'react-router-dom';
import ApiStatusIndicator from './ApiStatusIndicator';
import { useImpersonation } from '../hooks/useImpersonation';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
  backPath?: string;
  onBack?: () => void;  // Optional callback for custom back behavior (e.g., navigate(-1))
  actions?: React.ReactNode;  // Optional controls rendered in the header's top-right (e.g. a refresh button)
}

export default function PageHeader({ title, subtitle, children, backPath, onBack, actions }: PageHeaderProps) {
  const navigate = useNavigate();
  const { impersonating, isImpersonating, clearImpersonation } = useImpersonation();

  // Handle back button click - prefer onBack callback, fall back to backPath
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backPath) {
      navigate(backPath);
    }
  };
  // Use more bottom padding when there are children (extended headers like Today)
  const paddingClass = children ? 'pb-6' : 'pb-4';

  return (
    <header className={`w-full max-w-full overflow-hidden bg-gradient-to-r from-primary-600 to-primary-700 px-4 pt-4 text-white safe-area-inset-top ${paddingClass}`}>
      {/* Impersonation Banner */}
      {isImpersonating && impersonating && (
        <div className="mb-3 flex items-center justify-between rounded-lg bg-purple-600 px-3 py-2 overflow-hidden">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-sm font-medium truncate">Viewing as: {impersonating.name}</span>
          </div>
          <button
            onClick={clearImpersonation}
            className="rounded bg-purple-500 px-2 py-0.5 text-xs font-medium hover:bg-purple-400"
          >
            Stop
          </button>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 w-full max-w-full">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {(backPath || onBack) && (
            <button
              onClick={handleBack}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div className="min-w-0">
            {subtitle && <p className="text-sm text-primary-200 truncate">{subtitle}</p>}
            <h1 className="font-heading text-xl font-bold truncate">{title}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {actions}
          <ApiStatusIndicator />
          <Link
            to="/"
            className="flex items-center justify-center rounded-lg bg-white px-3 py-2 shadow-sm hover:bg-gray-50 transition-colors"
          >
            <img
              src="/images/logo.png"
              alt="RDM - Go to Home"
              className="h-9 w-auto object-contain"
            />
          </Link>
        </div>
      </div>
      {children}
    </header>
  );
}
